import { StepAction } from './stepAction';
import type { ApiStep } from 'clickim/src/common/models/apiStep';
import type { ApiResult, EventData } from 'clickim/src/common/playback/apiCall';

export class ApiStepAction extends StepAction<ApiStep> {
    private async runApiInAut(eventData: EventData) {
        eventData.withCredentials = true;
        const timeout = this.context.data.timeToPlayStep! + 3000;
        const { apiCall } = this.sessionPlayerInit;
        try {
            const autRes = await this.driver.executeCodeAsync(apiCall, timeout, eventData) as { value: ApiResult };
            const resp = autRes?.value;
            return resp || {};
        } catch (err) {
            if (err?.message?.includes('Javascript execution context no longer exists')) {
                throw new Error('The page refreshed or changed while executing this step. Please consider unchecking "Send via web page" if this is expected.');
            }
            throw err;
        }
    }

    private runApiInBg(eventData: EventData) {
        const { apiCall } = this.sessionPlayerInit;
        return apiCall(eventData);
    }

    override async performAction() {
        const { sessionPlayerInit: { commonConstants }, step, context } = this;

        const eventData: EventData = {
            id: step.id,
            url: context.apiUrl!,
            method: step.method,
            headers: context.apiHeaders,
            body: context.apiBody,
            timeout: context.data.maxTotalStepTime!,
            omitCookies: step.omitCookies,
            forceUseFetch: ['android', 'ios'].includes(context.project.type),
            formData: step.formData as EventData['formData'],
            fileUrls: context.fileUrls,
        };

        const resp = step.sendViaWebApp ? await this.runApiInAut(eventData) : await this.runApiInBg(eventData);
        const result = resp.result || {};
        const resultInfo = {
            method: step.method,
            status: result.status,
            url: step.url,
        };
        if (resp.success) {
            if (result.status === 0) {
                return {
                    result,
                    resultInfo,
                    shouldRetry: false,
                    success: false,
                    reason: 'Connection problem',
                    errorType: commonConstants.stepResult.API_REQUEST_NETWORK_ERROR,
                };
            }
            return {
                result,
                resultInfo,
                shouldRetry: false,
                success: true,
            };
        }
        return {
            result,
            resultInfo,
            shouldRetry: false,
            success: false,
            reason: result.error || commonConstants.error.REQUEST_TIMED_OUT,
            errorType: result.exception ? undefined : (result.error ? commonConstants.stepResult.API_FAILURE : commonConstants.stepResult.API_REQUEST_NETWORK_ERROR),
        };
    }
}
